import type { ReactNode } from "react";
import { Suspense, useMemo, useState } from "react";
import { Link, matchPath, useLocation } from "react-router-dom";
import { LogOut, Menu, User } from "lucide-react";
import type { SessionPayload } from "../api";
import { ChangePasswordDialog } from "./ChangePasswordDialog";
import { PageSpinner } from "../components/PageSpinner";
import { appRoutes } from "../routes";
import { classNames } from "../shared/classNames";
import { ExportCenterNoticeHost } from "../shared/exportCenterNotice";
import { Sidebar } from "./Sidebar";

/** Header labels from IANA zone: full desktop text and compact mobile offset. */
function formatTimezoneHeaderLabels(iana: string): { full: string; short: string } {
  const zone = String(iana || "").trim() || "Asia/Yangon";
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: zone,
      timeZoneName: "longOffset",
    }).formatToParts(new Date());
    const offset = parts.find((p) => p.type === "timeZoneName")?.value || "";
    const gmt = offset.replace(/^GMT/, "GMT").replace(/^UTC/, "GMT") || "GMT";
    return { full: `${gmt} - ${zone}`, short: gmt };
  } catch {
    return { full: zone, short: zone };
  }
}

export function Shell({
  session,
  children,
  onLogout,
}: {
  session: SessionPayload;
  children: ReactNode;
  onLogout: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [sidebarVisible, setSidebarVisible] = useState(true);
  const [changePassword, setChangePassword] = useState(false);
  const location = useLocation();
  const matched = useMemo(() => {
    for (const r of appRoutes) {
      const m = matchPath({ path: r.path, end: true }, location.pathname);
      if (m) return r;
    }
    return undefined;
  }, [location.pathname]);
  const systemTz = String(session.system_timezone || "Asia/Yangon").trim() || "Asia/Yangon";
  const tzLabels = useMemo(() => formatTimezoneHeaderLabels(systemTz), [systemTz]);
  return (
    <div className={classNames("internal-shell", sidebarVisible ? "has-sidebar" : "no-sidebar")}>
      <Sidebar
        session={session}
        open={open}
        showDesktop={sidebarVisible}
        onClose={() => setOpen(false)}
        onLogout={onLogout}
      />
      <div className="internal-content">
        <header className="internal-topbar">
          <div className="internal-topbar-row">
            <div className="internal-topbar-left">
              <button
                type="button"
                className="internal-icon-button mobile-menu"
                onClick={() => setOpen(true)}
                aria-label="Open menu"
              >
                <Menu className="h-5 w-5" />
              </button>
              <button
                type="button"
                className="internal-icon-button desktop-menu"
                onClick={() => setSidebarVisible((value) => !value)}
                aria-label={sidebarVisible ? "Hide sidebar" : "Show sidebar"}
              >
                <Menu className="h-4 w-4" />
              </button>
              <div className="internal-page-title">
                <div>{matched?.section || "KLT-RMS"}</div>
                <h1>{matched?.title || "Console"}</h1>
              </div>
            </div>
            <div className="internal-topbar-right">
              <Link
                to="/settings"
                className="internal-timezone-select"
                title={`${tzLabels.full} — change in System settings`}
                style={{ textDecoration: "none" }}
              >
                <span className="internal-timezone-full">{tzLabels.full}</span>
                <span className="internal-timezone-short">{tzLabels.short}</span>
              </Link>
              <div className="internal-user-pill">
                <User className="h-4 w-4 text-slate-500" />
                <span>{session.user?.username}</span>
                <span>{session.user?.role}</span>
              </div>
              <button
                type="button"
                onClick={() => setChangePassword(true)}
                className="internal-topbar-logout internal-topbar-password"
                aria-label="Change Password"
              >
                <span className="internal-password-full">Change Password</span>
                <span className="internal-password-short">Password</span>
              </button>
              <button
                type="button"
                onClick={onLogout}
                className="internal-topbar-logout internal-topbar-logout-only"
                aria-label="Logout"
              >
                <LogOut className="h-4 w-4" />
                <span className="internal-logout-label">Logout</span>
              </button>
            </div>
          </div>
        </header>
        <main className="internal-main">
          <ExportCenterNoticeHost />
          <Suspense fallback={<PageSpinner />}>{children}</Suspense>
        </main>
      </div>
      {changePassword ? (
        <ChangePasswordDialog
          session={session}
          onCancel={() => setChangePassword(false)}
          onDone={() => {
            setChangePassword(false);
            onLogout();
          }}
        />
      ) : null}
    </div>
  );
}
